/**
 * Alternate group inference (Phase 2 — RFC-108-G).
 *
 * The converter's tier-1/tier-2 CityGML ingest writes one `SceneItem` per
 * (theme, LoD) pair (e.g. `tokyo-shinjuku-bldg-lod1`, `…-bldg-lod2`,
 * `…-bldg-lod2-no-texture`). RFC-108-G says these should be grouped into one
 * panel row + variant dropdown by setting `alternateOfLayerId` on the
 * non-primary members. The current ingest does **not** write those fields,
 * so the viewer must infer them.
 *
 * This is a **viewer-side fallback**: any `alternateOfLayerId` /
 * `alternateLabel` / `alternatePreference` set by the envelope OR by the
 * converter writeback wins. The inference only fires when ALL members of a
 * potential group are unannotated.
 *
 * Heuristic
 * ---------
 *
 * 1. For each layer extract `(stem, lodToken, suffix, lodRank)` from the
 *    `tileset3d` endpoint URL (most reliable) OR the layer id, in that
 *    order. A "stem" is the URL path segment without the `-lod<N>[-suffix]`
 *    fragment. Layers without a recognised LoD token are ignored.
 * 2. Group layers by `stem`. Groups with fewer than 2 members are dropped.
 * 3. Within each group:
 *    - sort by `(lodRank desc, suffix preference)` so the **highest LoD with
 *      texture** lands at index 0 (textured > no-texture > lod1, etc.);
 *    - the index-0 entry becomes the **primary** (`alternateOfLayerId` =
 *      null); the rest get `alternateOfLayerId = primary.id` and an
 *      `alternatePreference` from 1 upwards.
 *    - `alternateLabel` is derived from `(lodToken, suffix)` in a stable,
 *      human-readable form: `LOD2`, `LOD2 (no texture)`, `LOD1`, etc.
 *
 * The module is intentionally pure — no React, no I/O — so it can be unit
 * tested without a Vite environment and called from either intake
 * (`gtwinToCatalog`) or the store (`setCatalog`).
 */

import type { Layer } from '@geosoft/viewer-sdk';

// ---------------------------------------------------------------------------
// Public types
// ---------------------------------------------------------------------------

export interface AlternateAnnotation {
  alternateOfLayerId: string | null;
  alternateLabel: string;
  alternatePreference: number;
}

// ---------------------------------------------------------------------------
// LoD parsing
// ---------------------------------------------------------------------------

interface ParsedLod {
  stem: string;
  lodToken: string;
  /** Numeric rank for sorting; LOD2 = 2, LOD3 = 3, missing = 0. */
  lodRank: number;
  /** Suffix after `lod<N>-`, e.g. "no-texture". Empty string = textured. */
  suffix: string;
  /** Combined source used for matching ("url" or "id"). */
  source: 'url' | 'id';
}

const LOD_PATTERN = /^(.*?)[-_]lod(\d+)(?:[-_](.+))?$/i;

/**
 * Extract the last URL path segment without a file extension, e.g.
 * `https://host/converter-output/3dcity-tier2/tokyo-shinjuku-bldg-lod1/tileset.json`
 * → `tokyo-shinjuku-bldg-lod1`.
 */
function tilesetUrlSlug(url: string | undefined): string | null {
  if (!url) return null;
  const trimmed = url.replace(/[?#].*$/, '').replace(/\/+$/, '');
  const segments = trimmed.split('/');
  // The slug is the last directory segment (the `tileset.json` part lives
  // one level below the directory we care about).
  const last = segments[segments.length - 1] ?? '';
  if (/^[a-z0-9_\-.]+\.[a-z0-9]+$/i.test(last) && segments.length >= 2) {
    return segments[segments.length - 2] ?? null;
  }
  return last || null;
}

function parseLod(layer: Layer): ParsedLod | null {
  const url = layer.endpoints.tileset3d;
  const slugFromUrl = tilesetUrlSlug(url);
  if (slugFromUrl) {
    const match = LOD_PATTERN.exec(slugFromUrl);
    if (match) {
      return {
        stem: match[1].toLowerCase(),
        lodToken: `lod${match[2]}`,
        lodRank: Number.parseInt(match[2], 10),
        suffix: (match[3] ?? '').toLowerCase(),
        source: 'url',
      };
    }
  }
  // Fallback: parse from the layer id (less reliable — converters embed
  // run-ids; the id may not contain a recognisable stem at all).
  const match = LOD_PATTERN.exec(layer.id);
  if (match) {
    return {
      stem: match[1].toLowerCase(),
      lodToken: `lod${match[2]}`,
      lodRank: Number.parseInt(match[2], 10),
      suffix: (match[3] ?? '').toLowerCase(),
      source: 'id',
    };
  }
  return null;
}

// ---------------------------------------------------------------------------
// Primary selection
// ---------------------------------------------------------------------------

/**
 * Ranking score: higher = "more authoritative". Highest LoD that carries
 * texture wins; untextured variants come after; LoD1 last; non-conforming
 * suffixes lose to clean ones.
 */
function memberScore(parsed: ParsedLod): number {
  const lodScore = parsed.lodRank * 10;
  let suffixPenalty = 0;
  if (parsed.suffix) {
    if (/no.?texture|notex|untextured/.test(parsed.suffix)) suffixPenalty = 4;
    else if (/draft|preview/.test(parsed.suffix)) suffixPenalty = 6;
    else suffixPenalty = 2; // unknown suffix — slight penalty so clean LoD wins
  }
  return lodScore - suffixPenalty;
}

function humaniseLod(parsed: ParsedLod): string {
  const upper = parsed.lodToken.toUpperCase();
  if (!parsed.suffix) return upper;
  const cleanSuffix = parsed.suffix.replace(/-/g, ' ');
  if (/no.?texture|notex|untextured/i.test(cleanSuffix)) return `${upper} (no texture)`;
  return `${upper} (${cleanSuffix})`;
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Infer alternate-group annotations for the supplied catalog. Returns a map
 * keyed by `layer.id` — only layers that joined an inferred group appear.
 *
 * Layers whose envelope already carries any alternate-* field are excluded
 * from inference: the writer (operator or converter) is the source of truth.
 *
 * Layers whose kind is not `3dtiles` are also skipped — multi-LoD is a 3D
 * Tiles concern; raster/vector "alternates" are out of scope for this
 * heuristic.
 */
export function inferAlternateAnnotations(
  layers: readonly Layer[],
): Map<string, AlternateAnnotation> {
  const annotations = new Map<string, AlternateAnnotation>();

  const eligible = layers.filter((layer) => {
    if (layer.kind !== '3dtiles') return false;
    // Already annotated by envelope/converter — don't override.
    if (
      typeof layer.alternateOfLayerId === 'string'
      || typeof layer.alternateLabel === 'string'
      || typeof layer.alternatePreference === 'number'
    ) {
      return false;
    }
    return true;
  });

  // Bucket by parsed stem.
  const buckets = new Map<string, Array<{ layer: Layer; parsed: ParsedLod }>>();
  for (const layer of eligible) {
    const parsed = parseLod(layer);
    if (!parsed) continue;
    const bucket = buckets.get(parsed.stem) ?? [];
    bucket.push({ layer, parsed });
    buckets.set(parsed.stem, bucket);
  }

  for (const [, members] of buckets) {
    if (members.length < 2) continue;

    // Sort high-score → low-score. Tie-break by lodRank desc, then id asc
    // for stable output.
    const ranked = [...members].sort((a, b) => {
      const scoreDiff = memberScore(b.parsed) - memberScore(a.parsed);
      if (scoreDiff !== 0) return scoreDiff;
      const lodDiff = b.parsed.lodRank - a.parsed.lodRank;
      if (lodDiff !== 0) return lodDiff;
      return a.layer.id.localeCompare(b.layer.id);
    });

    const primary = ranked[0];
    annotations.set(primary.layer.id, {
      alternateOfLayerId: null,
      alternateLabel: humaniseLod(primary.parsed),
      alternatePreference: 0,
    });
    for (let i = 1; i < ranked.length; i++) {
      annotations.set(ranked[i].layer.id, {
        alternateOfLayerId: primary.layer.id,
        alternateLabel: humaniseLod(ranked[i].parsed),
        alternatePreference: i,
      });
    }
  }

  return annotations;
}

/**
 * Apply inferred annotations to a layer list. Returns a new array of layers
 * with annotations merged. Layers whose envelope/converter already set
 * `alternateOfLayerId`/`alternateLabel`/`alternatePreference` are returned
 * unchanged.
 */
export function applyInferredAlternates(layers: readonly Layer[]): Layer[] {
  const annotations = inferAlternateAnnotations(layers);
  if (annotations.size === 0) return [...layers];
  return layers.map((layer) => {
    const ann = annotations.get(layer.id);
    if (!ann) return layer;
    return {
      ...layer,
      alternateOfLayerId: ann.alternateOfLayerId,
      alternateLabel: ann.alternateLabel,
      alternatePreference: ann.alternatePreference,
    };
  });
}
